import { Injectable, InjectionToken, Signal, inject, signal } from '@angular/core';
import { AUDIO_BLOB_STORE, PendingAudio } from './audio-blob-store';
import { uuidV4 } from '../features/helper/form/mutation-id';

export interface TranscribeResult { transcript: string; audioId: number; }

export interface AudioTranscriber {
  /** POST the WAV to the API and resolve with the transcript. */
  transcribe(p: PendingAudio): Promise<TranscribeResult>;
}

// Safe default so merely *injecting* AudioService (e.g. a component test that renders a
// mic but never flushes) never throws a "no provider" DI error. app.config overrides this
// with the real HTTP transcriber. The default rejects only if actually used.
export const AUDIO_TRANSCRIBER = new InjectionToken<AudioTranscriber>('AUDIO_TRANSCRIBER', {
  providedIn: 'root',
  factory: () => ({ transcribe: () => Promise.reject(new Error('AUDIO_TRANSCRIBER not configured')) }),
});

export interface TranscriptEvent {
  key: string; formId: number; afsField: string; slot: 'notes' | 'reason'; transcript: string;
  audioId: number;
}

@Injectable({ providedIn: 'root' })
export class AudioService {
  private store = inject(AUDIO_BLOB_STORE);
  private transcriber = inject(AUDIO_TRANSCRIBER);
  private listeners: ((e: TranscriptEvent) => void)[] = [];
  private flushing: Promise<void> | null = null;
  private again = false;

  /** True while any voice-capture is recording — used to disable other mic buttons. */
  readonly recordingActive = signal(false);

  private readonly _pendingKeys = signal<ReadonlySet<string>>(new Set<string>());
  /** Keys of clips captured but not yet transcribed. Hydrated from IndexedDB at startup so the
   *  chip survives a reload — the WAV outlives the component that recorded it. */
  readonly pendingKeys: Signal<ReadonlySet<string>> = this._pendingKeys.asReadonly();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void this.flush());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.flush();
      });
      void this.refreshPending();
      void this.flush(); // opportunistic drain on startup
    }
  }

  /** True when this field+slot has a clip awaiting transcription. */
  hasPending(formId: number, afsField: string, slot: 'notes' | 'reason'): boolean {
    const prefix = `${formId}/${afsField}/${slot}/`;
    for (const k of this._pendingKeys()) if (k.startsWith(prefix)) return true;
    return false;
  }

  /** Re-read the queued clips from the store. Public so a caller can hydrate the chip without
   *  waiting for a flush. */
  async refreshPending(): Promise<void> {
    this._pendingKeys.set(new Set((await this.store.listPending()).map(p => p.key)));
  }

  onTranscript(cb: (e: TranscriptEvent) => void): () => void {
    this.listeners.push(cb);
    return () => {
      const i = this.listeners.indexOf(cb);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  /** Persist a captured WAV locally and return its storage key. Enqueue only —
   *  flush() (or the online/startup triggers) performs the transcription upload. */
  async capture(formId: number, afsField: string, slot: 'notes' | 'reason',
                helperId: number, wav: Blob): Promise<string> {
    const key = `${formId}/${afsField}/${slot}/${uuidV4()}.wav`;
    await this.store.put({ key, formId, afsField, slot, helperId,
                           blob: wav, contentType: 'audio/wav' });
    await this.refreshPending();
    return key;
  }

  /** Transcribe every queued clip; drop each on success. Coalesces concurrent calls
   *  and re-runs once if flush() is called again mid-flight. No-ops while offline. */
  flush(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve();
    if (this.flushing) { this.again = true; return this.flushing; }
    this.flushing = this.run().finally(() => { this.flushing = null; });
    return this.flushing;
  }

  private async run(): Promise<void> {
    do {
      this.again = false;
      for (const p of await this.store.listPending()) {
        try {
          const { transcript, audioId } = await this.transcriber.transcribe(p);
          for (const cb of this.listeners)
            cb({ key: p.key, formId: p.formId, afsField: p.afsField, slot: p.slot, transcript, audioId });
          await this.store.delete(p.key);
          await this.refreshPending();
        } catch {
          /* leave queued for the next flush */
        }
      }
    } while (this.again);
  }
}
